import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import * as React from "react";
import { FinancesProps } from "pages/home/types";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import CloseIcon from "@mui/icons-material/Close";
import DialogContent from "@mui/material/DialogContent";
import Address from "pages/home/form/Address";
import Nationality from "pages/home/form/Nationality";
import WorkExperience from "pages/home/form/WorkExperience";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import { useEffect, useState } from "react";
import { DialogProps } from "@material-ui/core";
import { useMobile } from "hooks/useDisplay";

function Finances({
  annualIncome,
  assetWorth,
  onInputChange,
  formData,
  validationError,
  sourceFunds,
}: FinancesProps) {
  const [scroll, setScroll] = useState<DialogProps["scroll"]>("paper");
  const [productEligibilityModal, setProductEligibilityModal] =
    React.useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const mobile = useMobile();

  const handleAnnualIncomeChange = (event: SelectChangeEvent<string>) => {
    onInputChange("financial", "annual_income", event.target.value);
  };
  const handleSourceFundsChange = (event: SelectChangeEvent<string>) => {
    onInputChange("financial", "source_funds", event.target.value);
  };
  const handleAssetWorthChange = (event: SelectChangeEvent<string>) => {
    onInputChange("financial", "asset_worth", event.target.value);
  };

  const annualIncomeLocal = formData?.kyc?.financial?.annual_income;
  const assetWorthLocal = formData?.kyc?.financial?.asset_worth;

  useEffect(() => {
    const isDevUrl = window.location.href.includes(
      "https://dev.td365.finsatechnology.com"
    );

    const isEligible =
      annualIncomeLocal === "annual_income_1" &&
      assetWorthLocal === "asset_worth_1" &&
      process.env.REACT_APP_WHITELABEL === "TD365UK";

    if (isEligible) {
      if (isDevUrl) {
        setProductEligibilityModal(false);
      } else if (!confirmed) {
        setProductEligibilityModal(true);
      }
    } else {
      setConfirmed(false);
    }
  }, [annualIncomeLocal, assetWorthLocal, confirmed]);

  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  useEffect(() => {
    if (validationError) {
      setIsExpanded(true);
    }
  }, [validationError]);

  const handleAccordionChange = (
    event: React.SyntheticEvent,
    expanded: boolean
  ) => {
    setIsExpanded(expanded);
  };

  const handleClose = () => {
    setProductEligibilityModal(false);
    onInputChange("risk_acceptance", "confirmed", false);
  };
  const handleConfirm = () => {
    setConfirmed(true);
    setProductEligibilityModal(false);
    onInputChange("risk_acceptance", "confirmed", true);
  };

  return (
    <>
      <Dialog
        open={productEligibilityModal}
        onClose={handleClose}
        maxWidth="lg"
        scroll={scroll}
        PaperProps={{
          sx: {
            width: !mobile ? "40%" : "100%",
            maxWidth: "none",
          },
        }}
      >
        <DialogTitle
          sx={{ m: 0, p: 2, backgroundColor: "#242c35", color: "white" }}
          id="customized-dialog-title"
        >
          Product Eligibility
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={(theme) => ({
            position: "absolute",
            right: 8,
            top: 12,
            color: theme.palette.grey[500],
          })}
        >
          <CloseIcon sx={{ marginLeft: "5px" }} />
        </IconButton>
        <DialogContent dividers>
          <div>
            Financial Spread Bets and CFDs are complex instruments and come with
            a high risk of losing money rapidly due to leverage. As your
            financial status has changed the products we offer may no longer be
            suitable for you.{" "}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Edit</Button>
          <Button type="submit" onClick={handleConfirm}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
      <Accordion
        expanded={isExpanded}
        onChange={handleAccordionChange}
        elevation={0}
      >
        <AccordionSummary
          expandIcon={<ArrowDownwardIcon />}
          aria-controls="panel1-content"
          id="panel1-header"
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
            }}
          >
            <Typography>Financial Profile</Typography>

            <Typography
              variant="caption"
              sx={{ color: "gray", lineHeight: 1.8 }}
            >
              {annualIncomeLocal || assetWorthLocal ? `` : "Incomplete"}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box
            sx={{
              "& .MuiTextField-root": { m: 1 },
              display: "flex",
              flexDirection: "column",
            }}
          >
            <FormControl sx={{ m: 1, minWidth: 200 }}>
              <InputLabel id="annual-income-label">Annual Income</InputLabel>
              <Select
                onChange={handleAnnualIncomeChange}
                label="Annual Income"
                defaultValue=""
              >
                {annualIncome &&
                  annualIncome.map((item) => (
                    <MenuItem key={item.value} value={item.value}>
                      {item.label}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            <FormControl sx={{ m: 1, minWidth: 200 }}>
              <InputLabel id="asset-worth-label">
                Approximate Value of Assets
              </InputLabel>
              <Select
                onChange={handleAssetWorthChange}
                label="Approximate Value of Assets"
                defaultValue=""
              >
                {assetWorth &&
                  assetWorth.map((item) => (
                    <MenuItem key={item.value} value={item.value}>
                      {item.label}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            <FormControl sx={{ m: 1, minWidth: 200 }}>
              <InputLabel id="annual-income-label">Source of funds</InputLabel>
              <Select
                onChange={handleSourceFundsChange}
                label="Annual Income"
                defaultValue=""
              >
                {sourceFunds &&
                  sourceFunds.map((item) => (
                    <MenuItem key={item.value} value={item.value}>
                      {item.label}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            {validationError && (
              <Typography color="error" sx={{ mt: 1 }}>
                {validationError}
              </Typography>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
    </>
  );
}

export default Finances;
